/**
 * WaitlistForm — componente shared de waitlist (promovido de WaitlistClient em autenticacao-onboarding).
 *
 * Promoção: D8 do ADR landing-publica.
 * Original: apps/web/app/(app)/app/auth/register/WaitlistClient.tsx
 *
 * Usado em:
 *   - apps/web/app/(app)/app/auth/register/page.tsx (fluxo original)
 *   - apps/web/app/(public)/page.tsx (home landing, surface=home_hero)
 *   - apps/web/app/(public)/clubes/[slug]/page.tsx (surface=club_<slug>)
 *   - apps/web/app/(public)/pass/page.tsx (surfaces de plano FLP=ON)
 */
'use client';
import * as React from 'react';
import { BrandSquiggle, Button, Input, Banner } from '@brisa/ui';
import { gaEvent } from '@brisa/shared/observability/ga';
import { clarityEvent } from '@brisa/shared/observability/clarity';
import type { WaitlistSurface } from '../types/waitlist-surface';

export interface WaitlistFormProps {
  locale: 'pt' | 'es';
  surface: WaitlistSurface;
  copy: {
    headline: string;
    subhead: string;
    emailLabel: string;
    submit: string;
    success: string;
    duplicate: string;
    alreadyHaveAccount?: string;
  };
  /**
   * Server Action retorna `_track` quando sucesso (METRICS_SPEC §3 Evento 17).
   * Client emite GA4/Clarity após receber a resposta — mantém consent gate honrado.
   */
  action: (formData: FormData) => Promise<{
    ok: boolean;
    code?: string;
    _track?: { event: string; props: Record<string, unknown> };
  }>;
  /** Se true, não renderiza o link "Já tem conta?" (contexto não-auth) */
  hideLoginLink?: boolean;
}

export function WaitlistForm({
  locale: _locale,
  surface: _surface, // consumed by parent for tracking; destructured to exclude from spread
  copy,
  action,
  hideLoginLink = false,
}: WaitlistFormProps) {
  void _locale;
  void _surface;
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'success' | 'duplicate' | 'error'>(
    'idle',
  );
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg(null);
    const result = await action(new FormData(e.currentTarget));
    if (result.ok) {
      setStatus('success');
      // METRICS_SPEC §3 Evento 17 — emit client-side (após consent gate).
      if (result._track) {
        gaEvent(result._track.event, result._track.props);
        clarityEvent(result._track.event, result._track.props);
      }
    } else if (result.code === 'EMAIL_EXISTS') setStatus('duplicate');
    else if (result.code === 'RATE_LIMITED') {
      setStatus('error');
      setErrorMsg('Demasiadas tentativas. Tenta de novo em alguns minutos.');
    } else {
      setStatus('error');
      setErrorMsg('Algo correu mal. Tenta de novo.');
    }
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--brisa-sp-12)' }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 'var(--brisa-sp-6)' }}>
        <h2
          className="brisa-display"
          style={{
            fontSize: 'clamp(var(--brisa-text-xl), 6vw, var(--brisa-text-2xl))',
            color: 'var(--brisa-text)',
          }}
        >
          {copy.headline}
        </h2>
        <p
          style={{
            fontSize: 'var(--brisa-text-md)',
            color: 'var(--brisa-text-dim)',
            margin: 0,
            maxWidth: '36ch',
          }}
        >
          {copy.subhead}
        </p>
        <BrandSquiggle variant="horizontal" width={200} height={20} />
      </header>

      {status === 'success' && <Banner variant="success" message={copy.success} />}
      {status === 'duplicate' && <Banner variant="info" message={copy.duplicate} />}
      {status === 'error' && errorMsg && <Banner variant="warning" message={errorMsg} />}

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--brisa-sp-4)' }}>
        <Input
          type="email"
          name="email"
          label={copy.emailLabel}
          autoComplete="email"
          inputMode="email"
          required
        />
        <Button
          variant="primary"
          size="lg"
          fullWidth
          type="submit"
          disabled={status === 'submitting' || status === 'success'}
          data-event="waitlist-submitted"
        >
          {copy.submit}
        </Button>
      </form>

      {!hideLoginLink && copy.alreadyHaveAccount && (
        <div style={{ textAlign: 'center' }}>
          <a
            href="/app/auth/login"
            style={{
              color: 'var(--brisa-accent)',
              textDecoration: 'underline',
              fontSize: 'var(--brisa-text-sm)',
            }}
          >
            {copy.alreadyHaveAccount}
          </a>
        </div>
      )}
    </section>
  );
}
